import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { ArrowRight } from "lucide-react";

const API = "/api";

// Conversion block for blog articles: shows a few real catalog bouquets
// (image + name + price) from the article's city with an "Order" CTA.
export const ArticleProductsShowcase = ({ city, cityData, cityName, lang }) => {
  const [products, setProducts] = useState([]);

  useEffect(() => {
    if (!city) return;
    let active = true;
    axios
      .get(`${API}/products?city=${city}`)
      .then((res) => {
        if (!active) return;
        const list = (res.data || []).filter((p) => p.in_stock !== false && p.image);
        // Pick 4 bouquets spread across the catalog for visual variety
        let picked = [];
        if (list.length <= 4) {
          picked = list;
        } else {
          const step = Math.floor(list.length / 4);
          for (let i = 0; i < 4; i++) picked.push(list[i * step]);
        }
        setProducts(picked.filter(Boolean));
      })
      .catch(() => {});
    return () => {
      active = false;
    };
  }, [city]);

  if (!products.length) return null;

  const sym = cityData?.currencySymbol || "€";
  const cityHref = `/${city}${lang !== "ru" ? `?lang=${lang}` : ""}`;
  const heading =
    lang === "en"
      ? `Popular bouquets in ${cityName}`
      : lang === "tr"
      ? `${cityName} populer buketler`
      : `Популярные букеты ${cityData?.namePrepositional || cityName}`;
  const subHeading =
    lang === "en"
      ? "Same-day delivery · photo before sending"
      : lang === "tr"
      ? "Ayni gun teslimat · gondermeden once fotograf"
      : "Доставка в день заказа · фото перед отправкой";
  const orderLabel = lang === "en" ? "Order" : lang === "tr" ? "Siparis" : "Заказать";
  const allLabel =
    lang === "en"
      ? "View all bouquets"
      : lang === "tr"
      ? "Tum buketleri gor"
      : "Смотреть все букеты";

  const altFor = (name) =>
    lang === "en"
      ? `${name} — flower delivery in ${cityName}`
      : lang === "tr"
      ? `${name} — ${cityName} cicek teslimati`
      : `${name} — доставка цветов ${cityData?.namePrepositional || cityName}`;

  return (
    <section className="mt-12 pt-8 border-t border-gray-100" data-testid="article-products-showcase">
      <div className="mb-5">
        <h2 className="text-xl sm:text-2xl font-bold text-gray-900">{heading}</h2>
        <p className="text-sm text-gray-500 mt-1">{subHeading}</p>
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 sm:gap-4">
        {products.map((p) => {
          const name =
            lang === "en" && p.name_en
              ? p.name_en
              : lang === "tr" && p.name_tr
              ? p.name_tr
              : p.name;
          return (
            <Link
              to={cityHref}
              key={p.id}
              className="group flex flex-col rounded-2xl overflow-hidden border border-gray-100 hover:border-pink-200 hover:shadow-md transition-all bg-white"
              data-testid={`article-product-${p.id}`}
            >
              <div className="aspect-square overflow-hidden bg-gray-50">
                <img
                  src={p.image}
                  alt={altFor(name)}
                  loading="lazy"
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                />
              </div>
              <div className="p-3 flex flex-col flex-1">
                <h3 className="text-sm font-medium text-gray-900 leading-snug line-clamp-2 min-h-[2.5rem]">
                  {name}
                </h3>
                <div className="mt-2 flex items-center justify-between">
                  <span className="text-base font-bold text-gray-900">
                    {sym}
                    {p.price}
                  </span>
                  <span className="inline-flex items-center gap-1 text-xs font-semibold text-pink-600 group-hover:gap-1.5 transition-all">
                    {orderLabel}
                    <ArrowRight className="w-3.5 h-3.5" />
                  </span>
                </div>
              </div>
            </Link>
          );
        })}
      </div>
      <Link
        to={cityHref}
        className="inline-flex items-center gap-1.5 mt-5 text-sm font-semibold text-pink-600 hover:text-pink-700 transition-colors"
        data-testid="article-products-view-all"
      >
        {allLabel}
        <ArrowRight className="w-4 h-4" />
      </Link>
    </section>
  );
};
